// No-op kept only so the ~10 existing call sites at login/logout boundaries (sidebar sign-out
// buttons, SessionErrorWatcher, etc.) don't need touching. There used to be a real client-side
// session cache here (with its own history of being reverted 8 minutes after landing, per past
// incident notes) — it's gone now because the thing it cached, session.user.accessToken, no
// longer exists on the client (see core/config/auth.ts's session() callback). Client code has
// nothing left to invalidate.
export function invalidateClientSessionCache() {}

/**
 * Returns Authorization headers for fetchAPI() — server-side only. On the client this always
 * returns {} without ever calling getSession(): the real access token is deliberately never
 * exposed there (see core/config/auth.ts), so client-side authenticated /api/* calls instead
 * rely on the browser automatically sending its httpOnly NextAuth session cookie, which
 * app/api/[...path]/route.ts (core/api/api-proxy.ts) reads server-side via getToken() and
 * attaches as the real Authorization header. This used to make its own getSession() round trip
 * per call (deduped/cached client-side) purely to read a field that's no longer there — removing
 * it cuts a redundant /api/auth/session request out of every fetchAPI() call from the browser.
 *
 * next-auth imports stay dynamic so they're never resolved at module evaluation time — this
 * prevents next-auth/react from trying to parse NEXTAUTH_URL during the Next.js production build.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
    if (typeof window !== 'undefined') {
        return {};
    }

    // Server side — lazy-import to avoid build-time module evaluation. Never cache here: this
    // runs per-request, and module-level state would leak a session across requests/users. Uses
    // serverAuthOptions (not the plain authOptions used by useSession()/getSession()) — that
    // variant's session() callback still carries the raw access token, since it's only ever
    // reached from trusted server code, never from the client-facing NextAuth route.
    let token: string | null = null;
    try {
        const [{ getServerSession }, { serverAuthOptions }] = await Promise.all([
            import('next-auth/next'),
            import('@/core/config/auth'),
        ]);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const session: any = await getServerSession(serverAuthOptions);
        token = session?.user?.accessToken || null;
    } catch {
        token = null;
    }

    if (!token) {
        return {};
    }

    return {
        'Authorization': `Bearer ${token}`
    };
}
